import React, { useState, useEffect } from "react";
import { Search, X, Send, Users, Clock, XCircle } from "lucide-react";
import { useAppContext } from "../../../AppContext";
import { MyDomain } from "../../../App";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function AccountantInviteModal({
  visible,
  onClose,
  accountantId,
  onInviteSent,
}) {
  const { t } = useTranslation();
  const { Lang } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";

  const [contractors, setContractors] = useState([]);
  const [filteredContractors, setFilteredContractors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);

  useEffect(() => {
    if (visible) {
      fetchAvailableContractors();
    }
  }, [visible]);

  useEffect(() => {
    // Filter contractors based on search query (by ID)
    if (searchQuery.trim() === "") {
      setFilteredContractors([]); // Don't show anything until search
    } else {
      const filtered = contractors.filter((contractor) =>
        contractor.id?.toString().includes(searchQuery),
      );
      setFilteredContractors(filtered);
    }
  }, [searchQuery, contractors]);

  const fetchAvailableContractors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${MyDomain}AcountAntInvite/AccountantGetAllContractorsForInvite?accountantId=${accountantId}`,
      );

      if (response.data && response.data.allContractors) {
        setContractors(response.data.allContractors);
        console.log(
          "✅ Available contractors loaded:",
          response.data.allContractors,
        );
      }
    } catch (error) {
      console.error("❌ Error fetching available contractors:", error);
      alert(t("failedToLoadAvailableContractors"));
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async (contractorId) => {
    try {
      setSendingInvite(contractorId);

      const now = new Date();
      const day = String(now.getDate()).padStart(2, "0");
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const year = now.getFullYear();
      const formattedDate = `${day}-${month}-${year}`;

      const inviteData = {
        AccountAntId: accountantId,
        UserId: contractorId,
        DateSend: formattedDate,
      };

      const response = await axios.post(
        `${MyDomain}AcountAntInvite/CreateAcountAntInvite`,
        inviteData,
      );

      if (response.data) {
        alert(t("inviteSentSuccessfully"));

        // Update the contractor to show as pending instead of removing
        const updatedContractors = contractors.map((c) =>
          c.id === contractorId ? { ...c, hasPendingInvite: true } : c,
        );
        setContractors(updatedContractors);
        setFilteredContractors(
          updatedContractors.filter((c) =>
            c.id?.toString().includes(searchQuery),
          ),
        );

        if (onInviteSent) {
          onInviteSent();
        }
      }
    } catch (error) {
      console.error("❌ Error sending invite:", error);
      console.error("❌ Error response:", error.response?.data);

      if (error.response?.status === 400 && error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert(t("failedToSendInvite"));
      }
    } finally {
      setSendingInvite(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.trim().split(" ");
    if (words.length === 1) return name.substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  const renderContractorItem = (item) => (
    <div
      key={item.id}
      style={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        padding: "18px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        border: "1px solid #e5e8eb",
        transition: "all 0.2s ease",
        animation: "fadeInUp 0.3s ease-out",
        flexDirection: isRTL ? "row-reverse" : "row",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#007aff";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 122, 255, 0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e5e8eb";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          gap: "14px",
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        <div
          style={{
            width: "52px",
            height: "52px",
            borderRadius: "14px",
            backgroundColor: "rgba(0, 122, 255, 0.1)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              color: "#007aff",
            }}
          >
            {getInitials(item.name)}
          </span>
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            alignItems: isRTL ? "flex-end" : "flex-start",
          }}
        >
          <h3
            style={{
              fontSize: "17px",
              fontWeight: 600,
              color: "#1a1a1a",
              margin: "0 0 4px 0",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {item.name || t("unnamed")}
          </h3>
          {item.email && (
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: 0,
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {item.email}
            </p>
          )}
          {item.phoneNumber && (
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: 0,
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {item.phoneNumber}
            </p>
          )}
          <p
            style={{
              fontSize: "14px",
              color: "#666",
              margin: 0,
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t("id")}: {item.id}
          </p>
        </div>
      </div>

      {/* Show Pending or Invite button */}
      {item.hasPendingInvite ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "rgba(255, 149, 0, 0.1)",
            padding: "12px 18px",
            borderRadius: "12px",
            border: "1px solid #ff9500",
            whiteSpace: "nowrap",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <Clock size={18} color="#FF9500" />
          <span
            style={{
              color: "#ff9500",
              fontSize: "15px",
              fontWeight: 600,
            }}
          >
            {t("pending")}
          </span>
        </div>
      ) : (
        <button
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: sendingInvite === item.id ? "#999" : "#007aff",
            color: "#fff",
            padding: "12px 18px",
            borderRadius: "12px",
            border: "none",
            fontSize: "15px",
            fontWeight: 600,
            cursor: sendingInvite === item.id ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            whiteSpace: "nowrap",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
          onClick={() => sendInvite(item.id)}
          disabled={sendingInvite === item.id}
          onMouseEnter={(e) => {
            if (sendingInvite !== item.id) {
              e.currentTarget.style.backgroundColor = "#0056b3";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(0, 122, 255, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            if (sendingInvite !== item.id) {
              e.currentTarget.style.backgroundColor = "#007aff";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }
          }}
        >
          {sendingInvite === item.id ? (
            <div
              style={{
                width: "18px",
                height: "18px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderTopColor: "#fff",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
          ) : (
            <>
              <Send size={18} color="#fff" />
              <span>{t("invite")}</span>
            </>
          )}
        </button>
      )}
    </div>
  );

  if (!visible) return null;

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
          
          @keyframes inviteModalFadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes inviteModalSlideUp {
            from {
              transform: translateY(50px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
          animation: "inviteModalFadeIn 0.3s ease-out",
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "24px",
            width: "90%",
            maxWidth: "700px",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            animation: "inviteModalSlideUp 0.3s ease-out",
            overflow: "hidden",
            direction: isRTL ? "rtl" : "ltr",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "24px 28px",
              borderBottom: "1px solid #e5e8eb",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                color: "#1a1a1a",
                margin: 0,
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t("inviteContractors")}
            </h2>
            <button
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                backgroundColor: "#f5f7fa",
                border: "none",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e9ecef";
                e.currentTarget.style.transform = "rotate(90deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f7fa";
                e.currentTarget.style.transform = "rotate(0deg)";
              }}
            >
              <X size={28} color="#666" />
            </button>
          </div>

          {/* Search Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f7fa",
              borderRadius: "16px",
              padding: "0 16px",
              margin: "20px 28px 16px",
              height: "54px",
              gap: "12px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <Search size={20} color="#999" style={{ flexShrink: 0 }} />
            <input
              type="text"
              style={{
                flex: 1,
                fontSize: "16px",
                color: "#1a1a1a",
                border: "none",
                background: "transparent",
                outline: "none",
                textAlign: isRTL ? "right" : "left",
              }}
              placeholder={t("searchById")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery.length > 0 && (
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                  transition: "transform 0.2s ease",
                }}
                onClick={() => setSearchQuery("")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <XCircle size={20} color="#999" />
              </button>
            )}
          </div>

          {/* Contractors List */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "0 28px 28px",
            }}
          >
            {loading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "80px 20px",
                  minHeight: "300px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    border: "4px solid #e5e8eb",
                    borderTopColor: "#007aff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <p
                  style={{
                    marginTop: "16px",
                    fontSize: "16px",
                    color: "#666",
                    textAlign: "center",
                  }}
                >
                  {t("loading")}
                </p>
              </div>
            ) : searchQuery.trim() === "" ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "80px 20px",
                  minHeight: "300px",
                }}
              >
                <Search size={64} color="#CCC" />
                <p
                  style={{
                    fontSize: "17px",
                    color: "#999",
                    marginTop: "20px",
                    textAlign: "center",
                  }}
                >
                  {t("enterIdToSearch")}
                </p>
              </div>
            ) : filteredContractors.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "80px 20px",
                  minHeight: "300px",
                }}
              >
                <Users size={64} color="#CCC" />
                <p
                  style={{
                    fontSize: "17px",
                    color: "#999",
                    marginTop: "20px",
                    textAlign: "center",
                  }}
                >
                  {t("noContractorsFound")}
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {filteredContractors.map((item) => renderContractorItem(item))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
